import React, { useState } from "react";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import { Amplify } from 'aws-amplify';
import awsExports from './aws-exports';

import './App.css';
import heroImage from './assets/InDe-Landing-Page-Image-1.png';
import Login from './Login';
import InterestModal from './InterestModal';

Amplify.configure(awsExports);

// Demo Modal Component
function DemoModal({ isOpen, onClose, onRequestInfo }) {
  const videoUrl = 'https://www.youtube.com/embed/nUCq8mUKdNk';

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleRequestInfo = () => {
    onClose(); // Close demo modal first
    onRequestInfo(); // Then open interest modal
  };

  React.useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="demo-modal" onClick={handleBackdropClick}>
      <div className="demo-modal-content">
        <div className="demo-modal-header">
          <h3 className="demo-modal-title">InDe Platform Demo</h3>
          <button className="demo-modal-close" onClick={onClose}>
            &times;
          </button>
        </div>
        <div className="demo-video-container">
          <iframe
            src={videoUrl}
            title="InDe Platform Demo"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
        <div className="demo-modal-footer">
          <p>See how InDe transforms education through AI-powered personalization</p>
          <div className="demo-modal-buttons">
            <button className="btn-primary" onClick={handleRequestInfo}>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function LandingPageWrapper() {
  const navigate = useNavigate();
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [isInterestModalOpen, setIsInterestModalOpen] = useState(false);

  const openDemoModal = () => {
    if (window.gtag) window.gtag('event', 'watch_demo_click', { event_category: 'engagement' });
    setIsDemoModalOpen(true);
  };

  const closeDemoModal = () => {
    setIsDemoModalOpen(false);
  };

  const openInterestModal = () => {
    if (window.gtag) window.gtag('event', 'express_interest_click', { event_category: 'engagement' });
    setIsInterestModalOpen(true);
  };

  const closeInterestModal = () => {
    setIsInterestModalOpen(false);
  };

  const handleInterestSubmission = async (formData) => {
    try {
      // Get API endpoint from environment variable
      const apiUrl = process.env.REACT_APP_INTEREST_CAPTURE_API_URL;
      
      if (!apiUrl) {
        throw new Error('API endpoint not configured. Please check your .env file.');
      }
      
      // Call the Interest Capture API
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ 
          error: 'Network error', 
          message: `HTTP ${response.status}: ${response.statusText}` 
        }));
        throw new Error(errorData.message || `API request failed with status ${response.status}`);
      }

      const result = await response.json();

      // Track successful submission
      if (window.gtag) window.gtag('event', 'interest_form_submitted', { event_category: 'conversion' });

      // Close modal
      closeInterestModal();
      
      // Show success message based on action
      if (result.action === 'updated') {
        alert('Thank you! We\'ve updated your information and will be in touch soon.');
      } else {
        alert('Thank you for your interest! We\'ll be in touch soon.');
      }
      
    } catch (error) {
      // Show user-friendly error message
      let errorMessage = 'Sorry, there was an error processing your request. Please try again.';
      
      if (error.message.includes('API endpoint not configured')) {
        errorMessage = 'Service temporarily unavailable. Please try again later.';
      } else if (error.message.includes('Failed to fetch') || error.message.includes('Network error')) {
        errorMessage = 'Please check your internet connection and try again.';
      } else if (error.message.includes('Missing required fields')) {
        errorMessage = 'Please fill in all required fields.';
      } else if (error.message.includes('Invalid email')) {
        errorMessage = 'Please enter a valid email address.';
      }
      
      alert(errorMessage);
      
      // Re-throw to keep modal open
      throw error;
    }
  };

  return (
    <div className="main-container">
      {/* --- WELCOME SECTION --- */}
      <section className="welcome-section" style={{
        background: '#fff',
        padding: '2rem 1.5rem',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <div className="welcome-content" style={{ 
          maxWidth: '1000px', 
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          gap: '2rem'
        }}>
          {/* Left Logo */}
          <img 
            src="/assets/InDe-logo-1.png" 
            alt="InDe Logo" 
            style={{
              height: '125px',
              width: 'auto',
              flexShrink: 0
            }} 
          />
          
          {/* Center Content */}
          <div style={{ flex: 1, textAlign: 'center', minWidth: '0' }}>
            <h2 style={{
              fontSize: '1.75rem',
              fontWeight: '700',
              color: '#111827',
              marginBottom: '1rem'
            }}>
              Welcome to InDe!
            </h2>
            <p style={{
              fontSize: '1.15rem',
              color: '#4b5563',
              lineHeight: '1.6',
              margin: '0'
            }}>
              Here, we believe teachers should teach, not manage endless tasking. Our adaptive agent platform is designed to handle your administrative tasks so you can dedicate your time to doing what you chose to do: inspiring and educating your students.
            </p>
          </div>
          
          {/* Right Logo */}
          <img 
            src="/assets/InDe-logo-1.png" 
            alt="InDe Logo" 
            style={{
              height: '125px',
              width: 'auto',
              flexShrink: 0
            }} 
          />
        </div>
      </section>

      {/* --- HERO SECTION --- */}
      <section className="hero-section">
        <div className="hero-section">
          <div className="hero-content">
            <h1 className="hero-title">
              Co-Building Technologies <br />
              That Work for Teachers
            </h1>
            <p className="hero-desc">
              InDe's platform of adaptive agents combine teacher expertise with AI to deliver pedagogical knowledge at your fingertips, personalized educational content tailored to student-specific needs, and asynchronous inter-colleague collaboration so you have more time to engage with every student!
            </p>
            <div className="cta-buttons-and-features">
              <div className="cta-buttons">
                <button className="cta-btn" onClick={openInterestModal}>
                  Express Interest
                </button>
                <button className="cta-btn" onClick={openDemoModal}>
                  Watch Demo
                </button>
              </div>
            </div>
          </div>
          <div className="hero-placeholder">
            <img src={heroImage} alt="InDe Adaptive Agent Platform preview" className="hero-image" />
          </div>
        </div>
      </section>

      {/* --- FEATURES SECTION --- */}
      <section className="features-section">
        <h2 className="features-title">Powerful Features for Educators</h2>
        <p className="features-desc">
          InDe Adaptive Agent Platform gives you the tools to understand, track, and improve learning performance for all your students.
        </p>
        <div className="feature-cards-row">
          <div className="feature-card">
            <div className="feature-icon">&#128218;</div>
            <h3>Knowledge at Your Fingertips</h3>
            <p>Leverage evidence-based pedagogical knowledge retreival and seamlessly integrate into your instructional practices.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">&#129504;</div>
            <h3>AI-Personalized Content</h3>
            <p>Our adaptive agents analyze student profiles and learning patterns to create personalized content you approve.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">&#127979;</div>
            <h3>You Maintain Control</h3>
            <p>You always have the final say. Our agents suggest pathways and provide rationale, but you decide what's best for your students.</p>
          </div>
        </div>
      </section>

      {/* --- BOTTOM CTA SECTION --- */}
      <section className="bottom-cta-section">
        <h2 className="bottom-cta-title">Ready to Transform Learning Instruction?</h2>
        <p className="bottom-cta-desc">Join the growing body of educators using the InDe Adaptive Agent Platform to give yourself time back to focus on outcomes for your students.</p>
        <button className="cta-btn" onClick={openInterestModal}>Get Information on Joining Today</button>
      </section>

      {/* Demo Modal */}
      <DemoModal 
        isOpen={isDemoModalOpen} 
        onClose={closeDemoModal} 
        onRequestInfo={openInterestModal}
      />
      
      {/* Interest Modal */}
      <InterestModal 
        isOpen={isInterestModalOpen} 
        onClose={closeInterestModal}
        onSubmit={handleInterestSubmission}
      />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LandingPageWrapper />} />
        <Route path="/login" element={<Login />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
